import React, { useState } from "react";
import { Box, MenuItem, SxProps, Theme, Typography } from "@mui/material";
import { resendInvite } from "../../../api/derin";
import { collectionColors } from "../CollectionsTab/tokens";
import { errorMessageOf } from "../errorMessage";
import { useToast } from "../Toast";

/** What the actions menus know about a user, in the Users tab and an organization's. */
export interface InviteRecipient {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isEmailVerified: boolean;
}

/**
 * Sends someone their invitation email again, with the toast either way. Shared by
 * the Users tab and an organization's Users tab.
 */
export const useResendInvite = () => {
  const { showSuccess, showError } = useToast();
  const [sendingId, setSendingId] = useState<string | null>(null);

  const resend = async (user: InviteRecipient) => {
    if (sendingId) return;
    setSendingId(user.id);
    try {
      const { email } = await resendInvite(user.id);
      showSuccess(`Invitation sent again to ${email}`);
    } catch (err) {
      showError(errorMessageOf(err));
    } finally {
      setSendingId(null);
    }
  };

  return { resend, sendingId };
};

interface MenuItemProps {
  user: InviteRecipient | null | undefined;
  sendingId: string | null;
  onClick: () => void;
  sx?: SxProps<Theme>;
}

/**
 * "Resend invite" in a user's actions menu. Disabled once the account is activated:
 * the invitation link would only lead to "already activated".
 */
export const ResendInviteMenuItem: React.FC<MenuItemProps> = ({ user, sendingId, onClick, sx }) => {
  const activated = !!user?.isEmailVerified;
  return (
    <MenuItem disabled={!user || activated || sendingId === user.id} onClick={onClick} sx={sx}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {sendingId && sendingId === user?.id ? "Sending invite…" : "Resend invite"}
        {activated && (
          <Typography component="span" sx={{ fontSize: "12px", color: collectionColors.textSecondary }}>
            Already activated
          </Typography>
        )}
      </Box>
    </MenuItem>
  );
};
